import {
  type BaseFAQ,
  type FeaturedFAQ,
  getAllFaqCategories,
  getFeaturedFaqs,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/faq';
import { Accordion } from '@components/Accordion';
import { Breadcrumbs, type Crumb } from '@components/Breadcrumbs/Breadcrumbs';
import { Container } from '@components/Container';
import { Input } from '@components/FormComponents';
import { StandardLayout } from '@components/Layouts';
import {
  DarkBackgroundOverrides,
  PortableTextBlockRenderer,
} from '@components/PortableText/PortableText';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

export default function FaqsPage({
  categories,
  featuredFaqs,
  breadcrumbs,
}: {
  categories: BaseFAQ[];
  featuredFaqs: FeaturedFAQ[];
  breadcrumbs: Crumb[];
}): ReactElement {
  const router = useRouter();

  const rhfMethods = useForm<any>({
    mode: 'onTouched',
  });

  const onSubmit = (data, e) => {
    e.preventDefault();
    router.push(`/faqs/search-results?q=${rhfMethods.getValues().query}`);
  };

  const formattedFeaturedFaqs = featuredFaqs.map((f) => {
    return {
      title: f.title,
      className: '!text-white',
      content: (
        <>
          <PortableTextBlockRenderer
            content={f.body}
            componentsOverride={DarkBackgroundOverrides}
          />
        </>
      ),
    };
  });
  return (
    <>
      <Head>
        <title>Bulk Nutrients' Frequently Asked Questions</title>
        <meta
          name="title"
          content="Bulk Nutrients' Frequently Asked Questions"
        />
        <meta
          name="description"
          content="Find answers to common questions about Bulk Nutrients supplements, shipping, orders, ingredients, and more."
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/faqs`}
        />
      </Head>
      <Container className="dark:bg-grey-darkest">
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="mb-12.5 dark:bg-grey-darkest grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <h1>How can we help?</h1>
        <FormProvider {...rhfMethods}>
          <form
            onSubmit={rhfMethods.handleSubmit(onSubmit)}
            className="w-full sm:max-w-[600px] justify-self-center"
          >
            <Input
              name="query"
              type="search"
              label=""
              placeholder="Search Our FAQs"
              className="w-full"
            />
          </form>
        </FormProvider>
        <section className="full-bleed bg-black px-0 py-[25px] mt-8 text-white">
          <div className="mx-auto max-w-narrow-page px-4">
            <h2 className="pb-2 text-left font-bold uppercase text-white sm:text-[3rem] sm:leading-[4rem]">
              FAQs
            </h2>
            <Accordion defaultOpen={false} items={formattedFeaturedFaqs} />
          </div>
        </section>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 py-8">
          {categories.map((c) => (
            <div key={c.title} className="relative">
              <Link
                prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
                href={c.slug}
                aria-label={c.title}
                className="group"
              >
                <div className="card flex items-center justify-center min-h-[90px] p-4 bg-grey-light dark:border-grey-light dark:bg-grey-darkest hover:!border-orange active:!border-orange rounded-2xl">
                  <div className="text-lg font-bold text-center">{c.title}</div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </Container>
    </>
  );
}

FaqsPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async () => {
  const categories = await getAllFaqCategories();
  const featuredFaqs = await getFeaturedFaqs();

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'FAQs', path: '/faqs' },
  ];

  return {
    props: {
      categories,
      featuredFaqs,
      breadcrumbs,
    },
  };
};
